import React, { useState, useCallback, useEffect } from "react";
import CustomTable from "@/components/common/table/CustomTable";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import EditIcon from "@styles/all-images/icons/edit-icon-blue.svg";
import DeleteIcon from "@styles/all-images/icons/deleteblue.svg";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import FolderIcon from "@styles/all-images/icons/folderIcon.svg";
import FileIcon from "@styles/all-images/icons/file.svg";
import RightArrowIcon from "@styles/all-images/icons/right-arrow-blue.svg";
import CreateFolder from "./CreateFolder";
import FilePreviewModal from "@/components/common/FilePreviewModal";
import { toast } from "react-toastify";
import { SelectedDocument } from "@/components/common/inputs/FileUpload";
import { Skeleton } from "@mui/material";
import dateHandler from "@/composables/dateUtil";
import { NO_DATA_AVAILABLE } from "@/constants/constant-values";
import { usePermissionState } from "@/providers/PermissionProvider";
import { DocumentsPermissions } from "@/constants/permission-modules/base-permissions";

interface MyDriveTableProps {
  tabId?: number;
  isNested?: boolean;
}

interface ParentFolder {
  id: string;
  name: string;
}

const MyDriveTable: React.FC<MyDriveTableProps> = ({
  tabId,
  isNested = true,
}) => {
  const { id: folderId } = useParams();
  const dispatch = useDispatch<AppThunkDispatch>();
  const navigate = useNavigate();
  const [columns, setColumns] = useState<any[]>([]);
  const [forceReset, setForceReset] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "upload" | "edit">("create");
  const [initialData, setInitialData] = useState<any>(null);
  const [selectedDocument, setSelectedDocument] =
    useState<SelectedDocument | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [parentFolder, setParentFolder] = useState<ParentFolder[] | null>(null);
  const [folderDetails, setFolderDetails] = useState<any>(null);
  const [parentName, setParentName] = useState("");
  const { isPermitted } = usePermissionState();

  const { formatDate } = dateHandler();

  const handleView = useCallback(
    (row: any, isBreadcrumb: boolean = false) => {
      let path = "";
      if (row.document) {
        setDocuments([row.document]);
        setShowPreview(true);
        return;
      } else if (row.name === "My Drive") {
        path = `/document/my-drive`;
      } else if (isBreadcrumb) {
        path = `/document/my-drive/${row?.id}`;
      } else {
        path = `/document/my-drive/${row.folder?.id}`;
      }

      if (path !== window.location.pathname) {
        navigate(path);
      }
    },
    [navigate],
  );

  const handleEdit = useCallback((row: any) => {
    if (row.document) {
      setMode("upload");
      setIsCreateFolderOpen(true);
      setSelectedDocument(row.document);
    } else {
      setInitialData(row.folder);
      setMode("edit");
      setIsCreateFolderOpen(true);
    }
  }, []);

  const handleDelete = (row: any) => {
    setItemToDelete(row);
    setShowDeleteConfirm(true);
  };

  const handleBack = () => {
    navigate(`/document/my-drive`);
    // if (folderDetails?.parent) {
    //   navigate(`/document/my-drive/${folderDetails?.parent}`);
    // } else {
    //   navigate(`/document/my-drive`);
    // }
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);

    try {
      const { authenticatedCall } = actionsHandler(
        itemToDelete.document ? "documentsDelete" : "folderDelete",
      );
      const response = await dispatch(
        authenticatedCall({
          customParams: [
            itemToDelete.document
              ? itemToDelete.document.id
              : itemToDelete.folder.id + "/",
          ],
          method: "delete",
        }) as any,
      );

      if (response.ok) {
        toast.success(response.data.message || "Deleted successfully");
        setForceRefresh(true);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    }
  };

  const fetchDocumentsWrapper = useCallback(
    async (params: any) => {
      try {
        const { authenticatedCall } = actionsHandler("myDriveDocumentList");

        const queryParams = new URLSearchParams({
          page: params.page.toString(),
          limit: params.limit.toString(),
          ...(params.search && { search: params.search }),
          ...(params.sortColumn && {
            ordering: `${params.sortDirection === "desc" ? "-" : ""}${params.sortColumn}`,
          }),
        }).toString();
        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customParams: [folderId || tabId?.toString() || ""],
            customQuery: queryParams,
          }),
        );
        let responseData = { ...response };
        if (response.ok) {
          setFolderDetails(response.data.data);
          setParentName(response.data.data.name);
          setParentFolder(response.data.data.folder_path);

          const customResponse = {
            ...response.data,
            data: response.data.data.content || [],
          };

          responseData = {
            ...response,
            data: customResponse,
          };
        }

        return responseData;
      } catch (error) {
        console.error("Error fetching documents:", error);
        return {
          data: {
            data: [],
            meta: { count: 0, page: 1, total_pages: 1, limit: 10 },
          },
        };
      }
    },
    [dispatch, folderId, tabId],
  );

  const baseColumns = [
    {
      header: "Name",
      accessor: (row: any) => (
        <button onClick={() => handleView(row)}>
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-9 tw-h-9 tw-rounded-full tw-bg-[#F4EBFF]">
              <img
                src={row.document ? FileIcon : FolderIcon}
                alt={row.document ? "File Icon" : "Folder Icon"}
                className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
              />
            </span>
            <span className="tw-truncate tw-max-w-[300px]">
              {row.document ? row.document.file_name : row.folder.name}
            </span>
          </div>
        </button>
      ),
      sortable: true,
      sortKey: "name",
    },
    {
      header: "Uploaded on Date & Time",
      accessor: (row: any) =>
        row.created_at ? formatDate(row.created_at) : NO_DATA_AVAILABLE,
      sortable: true,
      sortKey: "created_at",
    },
    {
      header: "Actions",
      accessor: (row: any) => (
        <div className="tw-flex tw-items-center">
          <button onClick={() => handleView(row)} title="View">
            <img
              src={EyeIcon}
              alt="View"
              className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
            />
          </button>
          {isPermitted(DocumentsPermissions.UPDATE_MY_DRIVE_DOCUMENTS) && (
            <button onClick={() => handleEdit(row)} title="Edit">
              <img
                src={EditIcon}
                alt="Edit"
                className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
              />
            </button>
          )}
          {isPermitted(DocumentsPermissions.DELETE_MY_DRIVE_DOCUMENTS) && (
            <button onClick={() => handleDelete(row)} title="Delete">
              <img
                src={DeleteIcon}
                alt="Delete"
                className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
              />
            </button>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    setForceRefresh(true);
  }, [folderId]);

  const renderContent = () => (
    <>
      {renderBreadcrumb()}
      <CustomTable
        columns={baseColumns}
        fetchData={fetchDocumentsWrapper}
        forceRefresh={forceRefresh}
        setForceRefresh={setForceRefresh}
        forceReset={forceReset}
        setForceReset={setForceReset}
        addButton={
          isPermitted(DocumentsPermissions.CREATE_MY_DRIVE_DOCUMENTS)
            ? {
                text: "File Upload",
                onClick: () => {
                  setMode("upload");
                  setIsCreateFolderOpen(true);
                  setSelectedDocument(null);
                },
              }
            : undefined
        }
        secondaryAddButton={
          isPermitted(DocumentsPermissions.CREATE_MY_DRIVE_DOCUMENTS)
            ? {
                text: "Create Folder",
                onClick: () => {
                  setIsCreateFolderOpen(true);
                  setInitialData(null);
                  setMode("create");
                },
              }
            : undefined
        }
        noDataMessage="No items found"
        noSearchDataMessage="No matching items found"
      />
    </>
  );
  const renderBreadcrumb = () => {
    if (!isNested) return null;
    return (
      <div className="">
        <div className="tw-flex tw-items-center tw-gap-2">
          {parentFolder && (
            <>
              <span className="tw-w-4 tw-h-4"></span>
              <span className="tw-text-[#146C94] tw-font-medium tw-flex tw-items-center">
                {parentFolder.map((folder, index) => (
                  <React.Fragment key={folder.id}>
                    <span
                      onClick={() => handleView(folder, true)}
                      className="tw-cursor-pointer"
                    >
                      {folder.name}
                    </span>
                    {index < parentFolder.length - 1 && (
                      <img
                        src={RightArrowIcon}
                        alt=">"
                        className="tw-w-4 tw-h-4 tw-mx-2"
                      />
                    )}
                  </React.Fragment>
                ))}
              </span>
            </>
          )}
          {!parentFolder?.length && (
            <Skeleton
              variant="rectangular"
              width={500}
              height={30}
              className="tw-ml-6"
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {isNested ? (
        <div className="tw-relative">
          <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
            <div className="tw-px-6 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
              <div className="tw-flex tw-justify-between tw-items-center">
                <div className="tw-flex tw-items-center">
                  <img
                    src={BackIcon}
                    alt="Back Icon"
                    onClick={handleBack}
                    className="tw-cursor-pointer"
                  />
                  <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                    {parentName ? (
                      parentName
                    ) : (
                      <>
                        <Skeleton
                          variant="rectangular"
                          width={120}
                          height={30}
                        />
                      </>
                    )}
                  </h1>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-mt-[170px]">{renderContent()}</div>
        </div>
      ) : (
        renderContent()
      )}

      {isCreateFolderOpen && (
        <CreateFolder
          open={isCreateFolderOpen}
          onClose={() => setIsCreateFolderOpen(false)}
          mode={mode}
          parentId={folderId || tabId?.toString()}
          onSuccess={() => {
            setForceRefresh(true);
            setIsCreateFolderOpen(false);
          }}
          initialData={initialData}
          selectedDocument={selectedDocument || undefined}
        />
      )}

      {showPreview && (
        <FilePreviewModal
          document={documents}
          onClose={() => setShowPreview(false)}
        />
      )}

      {showDeleteConfirm && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-w-[400px]">
            <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b">
              <div></div>
              <h2 className="tw-text-base tw-font-semibold">Confirm Delete</h2>
              <button onClick={() => setShowDeleteConfirm(false)}>
                <svg
                  className="tw-w-5 tw-h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="tw-p-4 tw-text-center tw-flex tw-justify-center tw-gap-2 tw-py-5 tw-px-6">
              <h6 className="tw-text-[#232323] tw-text-base tw-font-semibold tw-gap-1">
                Are you sure you want to delete this{" "}
                {itemToDelete?.document ? "file" : "folder"}?
              </h6>
            </div>

            <div className="tw-flex tw-space-x-4 tw-py-5 tw-px-12">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="tw-flex-1 tw-px-4 tw-py-2 tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-w-[50%] tw-justify-center tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
              >
                {isDeleting ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MyDriveTable;
